// ---------------------------------------------------------------------------
// ConfirmDeleteDialog — shared confirmation dialog for destructive deletes.
// Presentational: the parent owns the delete mutation and passes the loading
// (`deleting`) + `error` state. Used by the Agents, Graders, and Judge Factory
// pages so the delete UX stays consistent. Callers supply the title and the
// body copy (as children).
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { useStyles } from './ConfirmDeleteDialog.style';

export interface ConfirmDeleteDialogProps {
  /** Whether the dialog is shown. */
  open: boolean;
  /** Dialog heading, e.g. "Delete agent". */
  title: string;
  /** Body copy — typically the confirmation question naming the entity. */
  children: ReactNode;
  /** True while the DELETE request is in flight. */
  deleting: boolean;
  /** Error from a failed delete, shown inline; `null` hides it. */
  error: string | null;
  /** Called when the dialog opens/closes (dismiss = `false`). */
  onOpenChange: (open: boolean) => void;
  /** Called to confirm the delete. The parent owns the mutation. */
  onConfirm: () => void;
  /** Primary button label; defaults to "Delete". */
  confirmLabel?: string;
}

export function ConfirmDeleteDialog({
  open,
  title,
  children,
  deleting,
  error,
  onOpenChange,
  onConfirm,
  confirmLabel = 'Delete',
}: ConfirmDeleteDialogProps) {
  const styles = useStyles();

  return (
    <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {children}
            {error !== null && (
              <Body1 className={styles.errorText} role="alert">
                {error}
              </Body1>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => onOpenChange(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
